import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarDays, LogOut, Mail, MapPin, Phone } from "lucide-react";
import { Navigate, useNavigate } from "react-router-dom";
import { adminApi } from "@/lib/api";

const statuses = {
  new: "Neu",
  contacted: "Kontaktiert",
  booked: "Gebucht",
  completed: "Erledigt",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const token = localStorage.getItem("admin-token") || "";
  const [filter, setFilter] = useState("");
  const query = useQuery({
    queryKey: ["admin-leads", filter],
    queryFn: () => adminApi.leads(token, filter || undefined),
    enabled: !!token,
  });
  const leads = useMemo(() => query.data || [], [query.data]);
  const mutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => adminApi.update(token, id, status),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-leads"] }),
  });
  const counts = useMemo(
    () => Object.keys(statuses).reduce((acc, key) => ({ ...acc, [key]: leads.filter(lead => lead.status === key).length }), {} as Record<string, number>),
    [leads],
  );

  if (!token) return <Navigate to="/admin/login" replace />;

  return <main className="admin-dashboard" data-testid="admin-dashboard">
    <header className="admin-header">
      <div><span className="eyebrow">Piccobello Chiemsee</span><h1 data-testid="admin-dashboard-title">Anfragen</h1></div>
      <button className="icon-button" aria-label="Abmelden" onClick={() => { localStorage.removeItem("admin-token"); navigate("/admin/login"); }} data-testid="admin-logout-button"><LogOut /></button>
    </header>
    <section className="admin-stats" data-testid="admin-stats">
      {Object.entries(statuses).map(([key, label]) => <button key={key} className={filter === key ? "active" : ""} onClick={() => setFilter(filter === key ? "" : key)} data-testid={`admin-filter-${key}`}><b>{filter ? (key === filter ? leads.length : 0) : counts[key] || 0}</b><span>{label}</span></button>)}
    </section>
    {query.isError && <p className="form-result error" role="alert" data-testid="admin-load-error">Sitzung abgelaufen. Bitte erneut anmelden.</p>}
    <section className="lead-list" data-testid="admin-lead-list">
      {leads.map(lead => <article className="lead-card" key={lead.id} data-testid={`lead-card-${lead.id}`}>
        <div className="lead-top">
          <div><span className={`status status-${lead.status}`} data-testid={`lead-status-${lead.id}`}>{statuses[lead.status]}</span><h2 data-testid={`lead-name-${lead.id}`}>{lead.name}</h2><p data-testid={`lead-service-${lead.id}`}>{lead.service} · {lead.area_sqm} m² · {lead.rooms} Räume</p></div>
          <select value={lead.status} onChange={event => mutation.mutate({ id: lead.id, status: event.target.value })} data-testid={`lead-status-select-${lead.id}`}>{Object.entries(statuses).map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select>
        </div>
        <div className="lead-meta"><a href={`mailto:${lead.email}`} data-testid={`lead-email-${lead.id}`}><Mail />{lead.email}</a><a href={`tel:${lead.phone}`} data-testid={`lead-phone-${lead.id}`}><Phone />{lead.phone}</a><span data-testid={`lead-date-${lead.id}`}><CalendarDays />{lead.preferred_date}</span><span data-testid={`lead-address-${lead.id}`}><MapPin />{lead.address}</span></div>
        {lead.message && <p className="lead-message" data-testid={`lead-message-${lead.id}`}>{lead.message}</p>}
      </article>)}
      {!query.isLoading && !leads.length && <div className="empty-state" data-testid="admin-empty-state">Noch keine Anfragen in dieser Ansicht.</div>}
    </section>
  </main>;
}